import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, ChevronRight, ChevronDown, Users as UsersIcon, UserCircle, Crown, Shield, Plus, Power, RotateCcw, Mail } from "lucide-react";
import CreateUserModal from "./users/CreateUserModal";
import ImportResultModal from "./users/ImportResultModal";

import { HierarchyView } from "./users/HierarchyView";
import { TableView } from "./users/TableView";

export default function LocalUsers() {
  const { user } = useAuth();
  const teamRole = (user?.team_role || "").toLowerCase();
  const isAdmin = user?.role === "admin" || user?.role === "manager" || user?.role === "hr_manager" || teamRole === "admin" || teamRole === "hr";
  const [view, setView] = useState("hierarchy");
  const [users, setUsers] = useState([]);
  const [tree, setTree] = useState(null);
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  useEffect(() => {
    const incoming = searchParams.get("q");
    if (incoming) { setQ(incoming); setView("table"); }
  }, [searchParams]);
  const qLower = q.trim().toLowerCase();
  const filteredUsers = qLower
    ? users.filter((u) =>
        [u.name, u.email, u.employee_code, u.employee_id, u.designation, u.department]
          .filter(Boolean).some((f) => String(f).toLowerCase().includes(qLower)))
    : users;

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [importResult, setImportResult] = useState(null); // {accounts_created, accounts_reset, default_password}

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/users", { params: { include_inactive: true } }), api.get("/team/hierarchy")]);
    setUsers(a.data || []);
    setTree(b.data);
  };
  useEffect(() => { load(); }, []);

  const updateUser = async (uid, field, value) => {
    try {
      await api.patch("/users/" + uid, { [field]: value });
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const toggleActive = async (u) => {
    const nextActive = !(u.is_active !== false);
    const verb = nextActive ? "REACTIVATE" : "SUSPEND";
    if (!window.confirm(`${verb} ${u.name} (${u.email})?\n\n${nextActive ? "They will regain login access immediately." : "They will be locked out of login and their active sessions cleared."}`)) return;
    try {
      if (nextActive) {
        await api.post(`/employees/${u.user_id}/reactivate`);
      } else {
        await api.delete(`/employees/${u.user_id}`);
      }
      toast.success(`${u.name} ${nextActive ? "reactivated" : "suspended"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${verb} failed`);
    }
  };

  const resetToDefault = async (u) => {
    if (!window.confirm(`Reset ${u.name}'s password to the default onboarding password (LD@2026!)?\n\nAll their active sessions will be cleared.`)) return;
    try {
      const { data } = await api.post(`/employees/${u.user_id}/reset-default-password`);
      toast.success(`Password reset to ${data.temp_password} — share via secure channel`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const emailCredentials = (u) => {
    // In-app confirm (browsers can silently suppress window.confirm — the
    // button then looked dead with no feedback at all).
    toast(`Email ${u.name} their login credentials?`, {
      id: `email-creds-${u.user_id}`,
      description: `Resets their password to a fresh random one and emails it to ${u.email}. Their active sessions will be signed out.`,
      duration: 10000,
      action: {
        label: "SEND",
        onClick: async () => {
          const tid = toast.loading(`Emailing credentials to ${u.name}…`);
          try {
            const { data } = await api.post(`/employees/${u.user_id}/email-credentials`, {});
            toast.success(`Credentials emailed to ${data.sent_to}`, { id: tid });
          } catch (e) {
            toast.error(e.response?.data?.detail || "Email failed", { id: tid });
          }
        },
      },
      cancel: { label: "CANCEL" },
    });
  };

  const salaryBoxFileRef = React.useRef(null);
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/users/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imported: " + data.created + " new, " + data.updated + " updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
  };

  const onSalaryBoxImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/employees/import-hr-master", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const created = data.created || 0;
      const updated = data.updated || 0;
      const errCount = (data.errors || []).length;
      const newAccounts = (data.accounts_created || []).length;
      if (created === 0 && updated === 0 && errCount > 0) {
        const firstErr = data.errors[0];
        const msg = typeof firstErr === "string" ? firstErr : (firstErr?.error || JSON.stringify(firstErr));
        toast.error(`SalaryBox import failed: 0 imported, ${errCount} errors. First: ${msg.slice(0, 200)}`);
      } else {
        toast.success(`SalaryBox import: ${created} new · ${updated} updated · ${newAccounts} accounts provisioned${errCount ? ` · ${errCount} errors` : ""}`);
      }
      if ((data.accounts_created || []).length > 0 || (data.accounts_reset || []).length > 0) {
        setImportResult(data);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "SalaryBox import failed");
    }
    e.target.value = "";
  };

  const downloadFile = async (url, name) => {
    const token = localStorage.getItem("session_token");
    const res = await api.get(url, { params: token ? { auth: token } : {}, responseType: "blob" });
    const u = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = u;
    a.download = name;
    a.click();
    URL.revokeObjectURL(u);
  };

  const enforceAdminWhitelist = async () => {
    if (!window.confirm(
      "Enforce admin whitelist?\n\nOnly landdrafters@gmail.com and santy@landdrafters.com will retain admin. " +
      "Every other user currently marked as admin will be demoted to jr_engineer + team_role='employee'.\n\nContinue?"
    )) return;
    try {
      const { data } = await api.post("/admin/enforce-admin-whitelist");
      if (data.count === 0) {
        toast.success("Whitelist already enforced — no changes.");
      } else {
        toast.success(`Demoted ${data.count} account${data.count === 1 ? "" : "s"}: ${data.demoted.map((d) => d.email).join(", ")}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Whitelist enforcement failed");
    }
  };

  const downloadNonAdminRoster = async () => {
    if (!window.confirm(
      "Download Excel with ALL non-admin users + freshly-generated login passwords?\n\n" +
      "This will reset the password for every non-admin active user. Admin accounts (landdrafters@, santy@) are NOT touched.\n\n" +
      "Continue?"
    )) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await api.post(
        "/employees/export-full",
        {},
        {
          params: { format: "xlsx", reset_passwords: true, include_admins: false, ...(token ? { auth: token } : {}) },
          responseType: "blob",
        },
      );
      const u = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = u;
      a.download = "team_non_admin_with_passwords.xlsx";
      a.click();
      URL.revokeObjectURL(u);
      toast.success("Roster downloaded — passwords were reset");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Roster export failed");
    }
  };

  const resetAndEmailAll = async () => {
    // Load available SMTP senders so the admin can pick which one to send from
    let senders = [];
    try {
      const { data } = await api.get("/invoice-senders");
      senders = data || [];
    } catch {
      toast.error("Failed to load SMTP senders");
      return;
    }
    if (senders.length === 0) {
      toast.error("Configure an SMTP sender in Invoices → Sender Accounts first");
      return;
    }
    const senderId = senders.length === 1
      ? senders[0].id
      : prompt(
          "Which SMTP sender should the credentials come from?\n\n" +
          senders.map((s, i) => `${i + 1}. ${s.display_name || s.email}`).join("\n") +
          "\n\nType the number:"
        );
    let sender = null;
    if (senders.length === 1) sender = senders[0];
    else {
      const idx = parseInt(senderId, 10) - 1;
      if (Number.isNaN(idx) || !senders[idx]) return;
      sender = senders[idx];
    }
    if (!window.confirm(
      `RESET every non-admin password AND email new credentials from ${sender.email}?\n\n` +
      "This will:\n" +
      "  1. Reset every non-admin active user's password to a fresh 12-char value.\n" +
      "  2. Sign out ALL their existing sessions.\n" +
      "  3. Send each of them an email with their new credentials.\n\n" +
      "⚠️  If a user's email delivery fails, their password IS still reset — they will\n" +
      "   need to contact you for their new credentials or receive a manual reset.\n\n" +
      "Admin accounts (landdrafters@, santy@) are NOT touched. Continue?"
    )) return;
    try {
      const { data } = await api.post("/employees/reset-and-email-all", null, {
        params: { sender_id: sender.id, include_admins: false, login_url: window.location.origin },
      });
      toast.success(
        `Reset ${data.passwords_reset} · Sent ${data.emails_sent}${data.failed_emails?.length ? ` · ${data.failed_emails.length} failed` : ""}`,
        { duration: 8000 },
      );
      if (data.failed_emails?.length) {
        console.error("reset-and-email-all failures:", data.failed_emails);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset-and-email failed");
    }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">ADMIN · LOCAL ACCOUNTS</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Local Users</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">{users.length} login accounts · the live org tree lives on <a href="/team" className="underline">/team</a></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onImport} className="hidden" data-testid="team-import-input" />
          <input ref={salaryBoxFileRef} type="file" accept=".xlsx,.csv" onChange={onSalaryBoxImport} className="hidden" data-testid="salarybox-import-input" />
          {isAdmin && (
            <>
              <button onClick={() => setShowCreateUser(true)} className="brut-btn px-3 py-2 text-xs flex items-center gap-1" data-testid="team-create-user-btn">
                <Plus className="w-3.5 h-3.5" /> NEW USER
              </button>
              <button onClick={() => fileRef.current && fileRef.current.click()} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="team-import-btn">
                <Upload className="w-3.5 h-3.5" /> IMPORT
              </button>
              <button onClick={() => salaryBoxFileRef.current && salaryBoxFileRef.current.click()} className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-amber-200 hover:bg-amber-300" data-testid="salarybox-import-btn" title="Import full SalaryBox employee sheet (with CTC, PF, ESIC, bank details)">
                <Upload className="w-3.5 h-3.5" /> SALARYBOX IMPORT
              </button>
              <button onClick={() => downloadFile("/employees/template", "salarybox_template.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="salarybox-template-btn" title="Download SalaryBox column template">
                <FileSpreadsheet className="w-3.5 h-3.5" /> SALARYBOX TEMPLATE
              </button>
              <button onClick={() => downloadFile("/users/template", "team_template.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="team-template-btn">
                <FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE
              </button>
              <button onClick={() => downloadFile("/users/export?format=xlsx", "team.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="team-export-btn">
                <Download className="w-3.5 h-3.5" /> EXPORT
              </button>
              <button onClick={downloadNonAdminRoster} className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-200 hover:bg-emerald-300" data-testid="export-non-admin-roster-btn" title="Excel of all non-admin users with freshly-generated passwords. Resets each user's password.">
                <Download className="w-3.5 h-3.5" /> ROSTER + PASSWORDS
              </button>
              <button onClick={resetAndEmailAll} className="brut-btn-gold px-3 py-2 text-xs flex items-center gap-1" data-testid="reset-and-email-all-btn" title="Reset every non-admin password AND email each user their new credentials via SMTP. Admin accounts are not touched.">
                <Mail className="w-3.5 h-3.5" /> RESET & EMAIL ALL
              </button>
              <button onClick={enforceAdminWhitelist} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1 bg-rose-100 hover:bg-rose-200" data-testid="enforce-admin-whitelist-btn" title="Demote any non-whitelisted admin (only landdrafters@ and santy@ stay admin)">
                <Shield className="w-3.5 h-3.5" /> ENFORCE ADMIN LIST
              </button>
              <label className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1 cursor-pointer bg-sky-100 hover:bg-sky-200" data-testid="import-hr-master-btn" title="Smart merge — auto-detects HR Master Register OR SalaryBox Bulk Update template. Strips Mr./Ms./Mrs. from names, matches by employee code / email / name, and fills only blank fields on existing users.">
                <Upload className="w-3.5 h-3.5" /> SMART HR IMPORT
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const fd = new FormData(); fd.append("file", file);
                  try {
                    const { data } = await api.post("/employees/import-hr-master", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    const tpl = (data.template || "").toUpperCase();
                    toast.success(`${tpl}: ${data.created} created · ${data.updated} updated · ${data.skipped || 0} skipped`);
                    load();
                  } catch (err) { toast.error(err.response?.data?.detail || "HR import failed"); }
                  e.target.value = "";
                }} />
              </label>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-0 border-b border-zinc-900">
        <button onClick={() => setView("hierarchy")} className={"px-4 py-2 text-sm font-mono font-bold border-t border-l border-r border-zinc-900 -mb-px " + (view === "hierarchy" ? "bg-yellow-200" : "bg-white")} data-testid="team-view-hierarchy">HIERARCHY</button>
        <button onClick={() => setView("table")} className={"px-4 py-2 text-sm font-mono font-bold border-t border-l border-r border-zinc-900 -mb-px " + (view === "table" ? "bg-yellow-200" : "bg-white")} data-testid="team-view-table">TABLE</button>
        <div className="ml-auto flex items-center pb-1">
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); const p = new URLSearchParams(searchParams); if (e.target.value) p.set("q", e.target.value); else p.delete("q"); setSearchParams(p, { replace: true }); }}
            placeholder="Search name / ID / email…"
            className="brut-input text-xs px-2 py-1 w-56"
            data-testid="users-search"
          />
          {q && <button onClick={() => { setQ(""); const p = new URLSearchParams(searchParams); p.delete("q"); setSearchParams(p, { replace: true }); }} className="ml-1 text-xs text-zinc-500 hover:text-zinc-900" title="Clear">✕</button>}
        </div>
      </div>

      {view === "hierarchy" && tree && <HierarchyView tree={tree} isAdmin={isAdmin} allUsers={users} reload={load} toggleActive={toggleActive} resetToDefault={resetToDefault} />}
      {view === "table" && <TableView users={filteredUsers} isAdmin={isAdmin} updateUser={updateUser} toggleActive={toggleActive} resetToDefault={resetToDefault} emailCredentials={emailCredentials} />}

      {showCreateUser && (
        <CreateUserModal allUsers={users} onClose={() => setShowCreateUser(false)} onCreated={() => { setShowCreateUser(false); load(); }} />
      )}

      {importResult && (
        <ImportResultModal result={importResult} onClose={() => setImportResult(null)} onSent={() => { setImportResult(null); load(); }} />
      )}
    </div>
  );
}

